import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getCall, postCall, putCall, deleteCall } from "../../utils/api";

const initialState = {
  data: [],
  currentConfig: null,
  error: "",
  status: "idle",
  deployStatus: "idle",
  triggerStatus: "idle",
  syncStatus: "idle",
};

export const getN8NConfigs = createAsyncThunk(
  "n8nCrawlConfig/getAll",
  async ({ source, crawl_type, is_active, page = 1, per_page = 50 } = {}) => {
    try {
      let url = `/admin/v1/n8n/configs?page=${page}&per_page=${per_page}`;
      if (source) url += `&source=${source}`;
      if (crawl_type) url += `&crawl_type=${crawl_type}`;
      if (is_active !== undefined) url += `&is_active=${is_active}`;
      const response = await getCall(url, false);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const getN8NConfigById = createAsyncThunk(
  "n8nCrawlConfig/getById",
  async (id) => {
    try {
      const response = await getCall(`/admin/v1/n8n/configs/${id}`, false);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const createN8NConfig = createAsyncThunk(
  "n8nCrawlConfig/create",
  async (data) => {
    try {
      const response = await postCall("/admin/v1/n8n/configs", data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const updateN8NConfig = createAsyncThunk(
  "n8nCrawlConfig/update",
  async ({ id, data, showToast = true }) => {
    try {
      const response = await putCall(`/admin/v1/n8n/configs/${id}`, data, showToast);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const deleteN8NConfig = createAsyncThunk(
  "n8nCrawlConfig/delete",
  async (id) => {
    try {
      await deleteCall(`/admin/v1/n8n/configs/${id}`);
      return id;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const deployN8NConfig = createAsyncThunk(
  "n8nCrawlConfig/deploy",
  async ({ id, showToast = true }) => {
    try {
      const response = await postCall(`/admin/v1/n8n/configs/${id}/deploy`, {}, showToast);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const triggerN8NConfig = createAsyncThunk(
  "n8nCrawlConfig/trigger",
  async (id) => {
    try {
      const response = await postCall(`/admin/v1/n8n/configs/${id}/trigger`, {});
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const activateN8NConfig = createAsyncThunk(
  "n8nCrawlConfig/activate",
  async (id) => {
    try {
      const response = await postCall(`/admin/v1/n8n/configs/${id}/activate`, {});
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const deactivateN8NConfig = createAsyncThunk(
  "n8nCrawlConfig/deactivate",
  async (id) => {
    try {
      const response = await postCall(`/admin/v1/n8n/configs/${id}/deactivate`, {});
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const testN8NConnection = createAsyncThunk(
  "n8nCrawlConfig/testConnection",
  async () => {
    try {
      const response = await getCall("/admin/v1/n8n/test-connection", false);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const syncN8NWorkflows = createAsyncThunk(
  "n8nCrawlConfig/sync",
  async () => {
    try {
      const response = await postCall("/admin/v1/n8n/sync", {}, false);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

export const getN8NWorkflows = createAsyncThunk(
  "n8nCrawlConfig/getWorkflows",
  async () => {
    try {
      const response = await getCall("/admin/v1/n8n/workflows", false);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const n8nCrawlConfigSlice = createSlice({
  name: "n8nCrawlConfig",
  initialState,
  reducers: {
    clearCurrentConfig: (state) => {
      state.currentConfig = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all configs
      .addCase(getN8NConfigs.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getN8NConfigs.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(getN8NConfigs.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Get by ID
      .addCase(getN8NConfigById.fulfilled, (state, action) => {
        state.currentConfig = action.payload?.data;
      })
      // Create
      .addCase(createN8NConfig.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createN8NConfig.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(createN8NConfig.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Update
      .addCase(updateN8NConfig.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateN8NConfig.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(updateN8NConfig.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Delete
      .addCase(deleteN8NConfig.fulfilled, (state, action) => {
        state.data.data = state.data.data?.filter(
          (config) => config.id !== action.payload
        );
      })
      // Deploy
      .addCase(deployN8NConfig.pending, (state) => {
        state.deployStatus = "loading";
      })
      .addCase(deployN8NConfig.fulfilled, (state) => {
        state.deployStatus = "succeeded";
      })
      .addCase(deployN8NConfig.rejected, (state) => {
        state.deployStatus = "failed";
      })
      // Trigger
      .addCase(triggerN8NConfig.pending, (state) => {
        state.triggerStatus = "loading";
      })
      .addCase(triggerN8NConfig.fulfilled, (state) => {
        state.triggerStatus = "succeeded";
      })
      .addCase(triggerN8NConfig.rejected, (state) => {
        state.triggerStatus = "failed";
      })
      // Activate/Deactivate
      .addCase(activateN8NConfig.fulfilled, (state, action) => {
        const config = state.data.data?.find(
          (c) => c.id === action.payload?.data?.id
        );
        if (config) {
          config.workflow_status = "active";
        }
      })
      .addCase(deactivateN8NConfig.fulfilled, (state, action) => {
        const config = state.data.data?.find(
          (c) => c.id === action.payload?.data?.id
        );
        if (config) {
          config.workflow_status = "deployed";
        }
      })
      // Sync workflows
      .addCase(syncN8NWorkflows.pending, (state) => {
        state.syncStatus = "loading";
      })
      .addCase(syncN8NWorkflows.fulfilled, (state) => {
        state.syncStatus = "succeeded";
      })
      .addCase(syncN8NWorkflows.rejected, (state) => {
        state.syncStatus = "failed";
      });
  },
});

export const { clearCurrentConfig } = n8nCrawlConfigSlice.actions;
export default n8nCrawlConfigSlice.reducer;
